import { Routes } from '@angular/router';
import { authGuard } from '@app/core/guards/auth.guard';
import { guestGuard } from '@app/core/guards/guest.guard';
import { roleGuard } from '@app/core/guards/role.guard';

export const routes: Routes = [
  {
    path: 'auth',
    canActivate: [guestGuard],
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('./features/auth/login/login').then((m) => m.LoginComponent),
      },
      { path: '', redirectTo: 'login', pathMatch: 'full' },
    ],
  },
  {
    path: '',
    loadComponent: () =>
      import('./layouts/admin-layout/admin-layout').then((m) => m.AdminLayoutComponent),
    canActivate: [authGuard],
    children: [
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/dashboard/dashboard').then((m) => m.DashboardComponent),
        data: { title: 'Admin Dashboard', subtitle: 'System Overview & Pending Actions' },
      },
      {
        path: 'client-users',
        loadComponent: () =>
          import('./features/client-users/users-management/users-management').then(
            (m) => m.UsersManagementComponent,
          ),
        canActivate: [roleGuard],
        data: {
          title: 'Users Management',
          subtitle: 'View clients, KYC status, and their transactions',
          requiredPermission: { feature: 'applications', action: 'view' },
        },
      },
      // Legacy alias — the static Onboarding page was replaced by Users
      // Management. Keep `/onboarding` working so cached bookmarks and any
      // external links land on the new surface.
      {
        path: 'onboarding',
        redirectTo: 'client-users',
        pathMatch: 'full',
      },
      {
        // CW-13d-admin — canonical KYC review surface backed by the real
        // /admin/users/pending-kyc endpoint and the CW-13c approve/reject
        // endpoints. Allowed roles: SUPER_ADMIN, ADMINISTRATOR,
        // COMPLIANCE_OFFICER (all map onto governance:view in ROLE_PERMISSIONS).
        // Approve / reject actions are further gated SUPER_ADMIN /
        // ADMINISTRATOR inside the component.
        path: 'kyc-review',
        loadComponent: () =>
          import('./features/compliance/kyc-review/kyc-review').then(
            (m) => m.KycReviewListComponent,
          ),
        canActivate: [roleGuard],
        data: {
          title: 'KYC Review',
          subtitle: 'Pending admin KYC review queue',
          requiredPermission: { feature: 'kyc_review', action: 'view' },
        },
      },
      {
        path: 'accounts',
        loadComponent: () =>
          import('./features/accounts/accounts').then((m) => m.AccountsComponent),
        canActivate: [roleGuard],
        data: {
          title: 'Account Management',
          subtitle: 'Trust accounts and sub-ledger management',
          requiredPermission: { feature: 'accounts', action: 'view' },
        },
      },
      {
        path: 'deposits',
        loadComponent: () =>
          import('./features/deposits/deposits').then((m) => m.DepositsComponent),
        canActivate: [roleGuard],
        data: {
          title: 'Deposit Verification',
          subtitle: 'Review and approve client deposits',
          requiredPermission: { feature: 'deposits', action: 'view' },
        },
      },
      {
        path: 'transactions',
        loadComponent: () =>
          import('./features/transactions/transaction-review/transaction-review').then(
            (m) => m.TransactionReviewComponent,
          ),
        canActivate: [roleGuard],
        data: {
          title: 'Transaction Review & Execution',
          subtitle: 'Approve and execute client transactions',
          requiredPermission: { feature: 'transfers', action: 'view' },
        },
      },
      {
        path: 'withdrawals',
        loadComponent: () =>
          import('./features/transactions/withdrawals/withdrawals').then(
            (m) => m.WithdrawalsComponent,
          ),
        canActivate: [roleGuard],
        data: {
          title: 'Withdrawal Processing',
          subtitle: 'Review and process client withdrawal requests',
          requiredPermission: { feature: 'transfers', action: 'view' },
        },
      },
      {
        path: 'reporting',
        loadComponent: () =>
          import('./features/audit/reporting/reporting').then((m) => m.ReportingComponent),
        canActivate: [roleGuard],
        data: {
          title: 'Reports & Audit Trail',
          subtitle: 'Transaction history and compliance reports',
          requiredPermission: { feature: 'audit', action: 'view' },
        },
      },
      {
        path: 'activity',
        loadComponent: () =>
          import('./features/audit/activity/activity').then((m) => m.ActivityComponent),
        canActivate: [roleGuard],
        data: {
          title: 'Activity Report',
          subtitle: 'Activity logs across admins, sub-admins, users and sub-users',
          requiredPermission: { feature: 'audit', action: 'view' },
        },
      },
      {
        path: 'activity/:subjectType/:userId',
        loadComponent: () =>
          import('./features/audit/user-activity/user-activity').then(
            (m) => m.UserActivityComponent,
          ),
        canActivate: [roleGuard],
        data: {
          title: 'User Activity',
          subtitle: 'Activity log for a specific user',
          requiredPermission: { feature: 'audit', action: 'view' },
        },
      },
      {
        path: 'users',
        loadComponent: () =>
          import('./features/governance/admin-users/admin-users').then(
            (m) => m.AdminUsersComponent,
          ),
        canActivate: [roleGuard],
        data: {
          title: 'Admin User Management',
          subtitle: 'Manage admin users and role permissions',
          requiredPermission: { feature: 'governance', action: 'view' },
        },
      },
      {
        path: 'crypto-management',
        loadComponent: () =>
          import('./features/governance/crypto-management/crypto-management').then(
            (m) => m.CryptoManagementComponent,
          ),
        canActivate: [roleGuard],
        data: {
          title: 'Crypto Wallet Management',
          subtitle: 'Manage deposit wallet addresses for crypto assets',
          requiredPermission: { feature: 'governance', action: 'view' },
        },
      },
      {
        path: 'fee-settings',
        loadComponent: () =>
          import('./features/governance/fee-settings/fee-settings').then(
            (m) => m.FeeSettingsComponent,
          ),
        canActivate: [roleGuard],
        data: {
          title: 'Fee Settings',
          subtitle: 'Configure fees, exchange rates, and transaction limits',
          requiredPermission: { feature: 'governance', action: 'view' },
        },
      },
      {
        path: 'system-settings',
        loadComponent: () =>
          import('./features/governance/system-settings/system-settings').then(
            (m) => m.SystemSettingsComponent,
          ),
        canActivate: [roleGuard],
        data: {
          title: 'System Settings',
          subtitle: 'Security, maintenance, and system health',
          requiredPermission: { feature: 'governance', action: 'view' },
        },
      },
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
    ],
  },
  { path: '**', redirectTo: 'auth/login' },
];
